/** Calendar feed, layers and external events — V5-03. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CalendarEventSource, CalendarLayer, CalendarProvider } from '@virtuetasks/types';
import { api } from '@/lib/api';

export interface FeedStatus {
  isSet: boolean;
  createdAt: string | null;
}

export interface CalendarEventView {
  _id: string;
  source: CalendarEventSource;
  title: string;
  startsAt: string;
  endsAt: string;
  inviteeName: string | null;
  meetingUrl: string | null;
  status: 'confirmed' | 'cancelled';
  /** V6-07 — provider events carry these; a Calendly booking does not. */
  location: string | null;
  isAllDay: boolean;
  /** The title already reads "Busy"; this only explains why (V6-07 §1). */
  isPrivate: boolean;
}

export function useFeedStatus() {
  return useQuery({
    queryKey: ['calendar', 'feed'],
    queryFn: async () => (await api.get<FeedStatus>('/calendar/feed')).data,
  });
}

/**
 * Returns the full URL — the only time the token is ever visible.
 *
 * It is stored hashed, so there is no "show it again" to build. The screen has
 * to make that plain, or someone closes the dialog and files a bug.
 */
export function useRegenerateFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<{ url: string }>('/calendar/feed/regenerate', {})).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar', 'feed'] }),
  });
}

export function useCalendarLayers() {
  return useQuery({
    queryKey: ['calendar', 'layers'],
    queryFn: async () => (await api.get<{ layers: CalendarLayer[] }>('/calendar/layers')).data,
  });
}

export function useSetCalendarLayers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (layers: CalendarLayer[]) =>
      (await api.put<{ layers: CalendarLayer[] }>('/calendar/layers', { layers })).data,
    onSuccess: (data) => qc.setQueryData(['calendar', 'layers'], data),
  });
}

export function useCalendarEvents(from: Date, to: Date, enabled = true) {
  return useQuery({
    queryKey: ['calendar', 'events', from.toISOString(), to.toISOString()],
    queryFn: async () =>
      (
        await api.get<CalendarEventView[]>(
          `/calendar/events?from=${from.toISOString()}&to=${to.toISOString()}`,
        )
      ).data,
    enabled,
  });
}

/* ── Calendly, workspace-level (§2) ────────────────────── */

export interface CalendlySettings {
  webhookUrl: string;
  signingKey: { isSet: boolean; masked: string | null };
  enabled: boolean;
}

export interface WebhookDeliveryView {
  _id: string;
  provider: string;
  eventType: string | null;
  outcome: string;
  detail: string | null;
  receivedAt: string;
}

export function useCalendlySettings() {
  return useQuery({
    queryKey: ['calendly', 'settings'],
    queryFn: async () => (await api.get<CalendlySettings>('/settings/integrations/calendly')).data,
  });
}

export function useSaveCalendly() {
  const qc = useQueryClient();
  return useMutation({
    // `null` clears; the untouched sentinel keeps — V10 §11.
    mutationFn: async (input: { signingKey?: string | null; enabled?: boolean }) =>
      (await api.patch<CalendlySettings>('/settings/integrations/calendly', input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendly'] });
    },
  });
}

export function useCalendlyDeliveries() {
  return useQuery({
    queryKey: ['calendly', 'deliveries'],
    queryFn: async () =>
      (await api.get<WebhookDeliveryView[]>('/settings/integrations/calendly/deliveries')).data,
    // Someone on this screen is usually mid-setup, watching for the first
    // delivery to land.
    refetchInterval: 10_000,
  });
}

/* ── Two-way sync, per person (V6-07 §2, §4) ───────────── */

export interface ConnectedCalendarView {
  calendarId: string;
  name: string;
  isPrimary: boolean;
  selected: boolean;
  kind: string | null;
}

export interface ConnectionHealthView {
  tone: 'ok' | 'warn' | 'error';
  summary: string;
  degraded: boolean;
}

export interface CalendarConnectionView {
  _id: string;
  provider: CalendarProvider;
  status: 'connected' | 'needs_reauth' | 'disconnected';
  accountEmail: string;
  calendarUrl: string | null;
  lastSyncAt: string | null;
  health: ConnectionHealthView;
  /** V8-04 §2 — "N events from Primary", or why there are none. */
  inbound?: string | null;
  calendars: ConnectedCalendarView[];
}

export interface CalendarConnectionsView {
  configured: Record<CalendarProvider, boolean>;
  connections: CalendarConnectionView[];
}

export function useCalendarConnections() {
  return useQuery({
    queryKey: ['calendar', 'connections'],
    queryFn: async () =>
      (await api.get<CalendarConnectionsView>('/calendar/connections')).data,
  });
}

/**
 * Starts the OAuth flow.
 *
 * The server builds the URL because only it holds the client id and can seal
 * the `state`; the browser's only job is to go there.
 */
export function useStartCalendarConnect() {
  return useMutation({
    mutationFn: async (provider: CalendarProvider) =>
      (await api.post<{ url: string }>(`/calendar/connections/${provider}/start`, {})).data,
  });
}

export function useSelectCalendars() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { provider: CalendarProvider; selected: string[] }) =>
      (
        await api.put<CalendarConnectionView>(
          `/calendar/connections/${input.provider}/calendars`,
          { selected: input.selected },
        )
      ).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

export function useSyncCalendarNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (provider: CalendarProvider) =>
      (
        await api.post<{ outcome: { ran: boolean; detail: string } }>(
          `/calendar/connections/${provider}/sync`,
          {},
        )
      ).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

export function useDisconnectCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { provider: CalendarProvider; deleteCalendar: boolean }) =>
      (
        await api.delete<{ deletedCalendar: boolean; removedEvents: number }>(
          `/calendar/connections/${input.provider}`,
          { deleteCalendar: input.deleteCalendar },
        )
      ).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

export interface CalendarSyncLogRow {
  _id: string;
  provider: CalendarProvider;
  event: string;
  outcome: 'ok' | 'skipped' | 'failed';
  detail: string | null;
  count: number | null;
  createdAt: string;
}

export function useCalendarSyncLog(enabled = true) {
  return useQuery({
    queryKey: ['calendar', 'connections', 'log'],
    queryFn: async () => (await api.get<CalendarSyncLogRow[]>('/calendar/connections/log')).data,
    enabled,
  });
}
